import React, { ChangeEvent, FormEvent, useCallback, useEffect, useState } from 'react'
import DocumentTitle from 'react-document-title'
import { FormattedMessage, useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import * as Sentry from '@sentry/browser'
import { Scope } from '@sentry/hub'
import { AxiosError, AxiosResponse } from 'axios'

import selector from './selector'
import useStyles from './styles'
import { IForm, SigninFields } from './types'

import { authDashlyUser, identifyDashlyUser, Mixpanel, trackDashlyEvent } from '@/analytics'
import { Button, BUTTON_SIZE, BUTTON_TYPE, BUTTON_VARIANT } from '@/components/ButtonSMB'
import { FieldHelpText } from '@/components/FieldHelpText'
import { FieldLabel } from '@/components/FieldLabel'
import { Input, INPUT_SIZE, INPUT_VARIANT } from '@/components/InputSMB'
import SupportFooter from '@/components/SupportFooter'
import { EMAIL_REGEX } from '@/const'
import { Routes } from '@/const/routes'
import { getBrowserLanguage } from '@/locales/helpers'
import { getReferral } from '@/store/referral'
import { MyThunkDispatch } from '@/store/store'
import { authorizeUser, getProfile, getUserAvailablePlugins, getUserInfo, getUserPlugins } from '@/store/user'
import { CloseDocType, ISigninResponse, IUserInfoResponse } from '@/store/user/types'
import { DashlyQuestEvents, DashlyQuestProps } from '@/types'
import { baseCatchError } from '@/utils'

const Signin = () => {
  const { authorized } = useSelector(selector)
  const classes = useStyles()
  const intl = useIntl()
  const dispatch: MyThunkDispatch = useDispatch()
  const history = useHistory()
  const location = useLocation<any>()

  const [form, setForm] = useState<IForm>({
    email: {
      value: '',
      hasError: false,
      touched: false,
      errorText: intl.formatMessage({ id: 'signIn.root.emailError' }),
    },
    password: {
      value: '',
      hasError: false,
      touched: false,
      errorText: intl.formatMessage({ id: 'signIn.root.passwordError' }),
    },
  })
  const [nonFieldError, setNonFieldError] = useState('')

  useEffect(() => {
    if (authorized) {
      const pathname = location?.state?.from?.pathname || Routes.ACCOUNTS
      history.push(pathname)
    }
  }, [])

  const isFieldValid = useCallback(
    (field: SigninFields, value: string) => {
      if (!value) {
        return false
      }

      if (field === 'email') {
        return !!value.match(EMAIL_REGEX)
      }

      return true
    },
    [form]
  )

  const inputChangeHandler = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const { value, name } = event.target
      const formItem = form[name as SigninFields]

      setForm({
        ...form,
        [name]: {
          ...formItem,
          value,
          touched: true,
          hasError: formItem.touched && !isFieldValid(name as SigninFields, value),
        },
      })
    },
    [form]
  )

  const blurHandler = useCallback(
    (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { name, value } = event.target
      const formItem = form[name as SigninFields]

      setForm({
        ...form,
        [name]: {
          ...formItem,
          touched: true,
          hasError: !isFieldValid(name as SigninFields, value),
        },
      })
    },
    [form]
  )

  const submitHandler = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault()

      const { email, password } = form

      const isValid = isFieldValid('email', email.value) && isFieldValid('password', password.value)

      if (isValid) {
        const { email, password } = form

        dispatch(
          authorizeUser({
            email: email.value,
            password: password.value,
          })
        )
          .then((response: AxiosResponse<ISigninResponse>) => {
            if (response.status === 200) {
              dispatch(getUserInfo())
                .then((userResponse: AxiosResponse<IUserInfoResponse>) => {
                  if (userResponse.status === 200) {
                    Sentry.configureScope((scope) => {
                      scope.setUser({ email: email.value.toLocaleLowerCase() })
                    })
                    Mixpanel.identify(email.value)

                    const winw = window
                    const browserLanguage = getBrowserLanguage()

                    const { id, has_profile, first_name } = userResponse.data
                    const authKey = process.env.REACT_APP_DASHLY_AUTH_KEY
                    authDashlyUser({ userId: id })

                    if (winw.dashly && winw.dashly.auth && id && authKey && !has_profile) {
                      trackDashlyEvent(DashlyQuestEvents.AUTH, {
                        $email: email.value.toLowerCase(),
                        $name: first_name,
                      })
                    }

                    identifyDashlyUser({
                      [DashlyQuestProps.DEVICE_LANGUAGE]: browserLanguage,
                    })

                    if (has_profile) {
                      dispatch(getProfile())
                        .then((profileResponse: AxiosResponse) => {
                          const { closedoc_type } = profileResponse.data

                          Mixpanel.set({ $email: email.value.toLowerCase() })
                          Mixpanel.identify(email.value)

                          trackDashlyEvent(DashlyQuestEvents.AUTH, {
                            $email: email.value.toLowerCase(),
                            $name: first_name,
                          })

                          identifyDashlyUser({
                            [DashlyQuestProps.DEVICE_LANGUAGE]: browserLanguage,
                          })

                          if (closedoc_type !== CloseDocType.spend) {
                            dispatch(getReferral()).catch(baseCatchError)
                          }
                        })
                        .catch((error: AxiosError) => {
                          Sentry.configureScope((scope: Scope) => {
                            scope.setExtra('response', error.response)
                          })
                          return Promise.reject(error)
                        })

                      dispatch(getUserPlugins()).catch(baseCatchError)
                      dispatch(getUserAvailablePlugins()).catch(baseCatchError)
                    }
                  }
                })
                .catch((error: AxiosError) => {
                  Sentry.configureScope((scope: Scope) => {
                    scope.setExtra('response', error.response)
                  })

                  return Promise.reject(error)
                })
            }

            return response
          })
          .catch((error: AxiosError<any>) => {
            const response = error.response

            if (response?.status === 403) {
              // 403 error happens, if employee authorized in admin interface
              setNonFieldError(intl.formatMessage({ id: 'common.adminError' }))
            } else if (response && response.status >= 500) {
              setNonFieldError(intl.formatMessage({ id: 'common.requestError' }))
            } else {
              if (response?.data) {
                const errors = Object.keys(response?.data).reduce(
                  (acc: string[], key: string) => [...acc, response?.data[key]],
                  []
                )
                setNonFieldError(errors.join('. '))
              }

              if (response?.status === 400) {
                Sentry.withScope(() => {
                  Sentry.configureScope((scope: Scope) => [scope.setUser({ email: email.value.toLocaleLowerCase() })])
                  Sentry.setExtra('response', response)
                  Sentry.captureMessage(`Ошибка авторизации`, Sentry.Severity.Info)
                })
              }
            }

            Sentry.configureScope((scope: Scope) => {
              scope.setExtra('response', error.response)
            })

            return Promise.reject(error)
          })
      }

      return
    },
    [form]
  )

  const formChangeHandler = useCallback(() => {
    setNonFieldError('')
  }, [form])

  return (
    <DocumentTitle title={intl.formatMessage({ id: 'signIn.root.pageTitle' })}>
      <div className={classes.root}>
        <div className={classes.container}>
          <div className={classes.header}>
            <h3 className={classes.title}>
              <FormattedMessage id="signIn.root.title" />
            </h3>
            <p className={classes.description}>
              <FormattedMessage id="signIn.root.subTitle" />
            </p>
          </div>
          <form onSubmit={submitHandler} onChange={formChangeHandler}>
            <div className={classes.inputGroup}>
              <div>
                <FieldLabel htmlFor="email" className={classes.inputLabel}>
                  <FormattedMessage id="signIn.root.emailLabel" />
                </FieldLabel>
                <Input
                  name="email"
                  id="email"
                  variant={INPUT_VARIANT.grey}
                  size={INPUT_SIZE.size48}
                  fullWidth={true}
                  placeholder={intl.formatMessage({ id: 'signIn.root.email' })}
                  value={form.email.value}
                  onChange={inputChangeHandler}
                  onBlur={blurHandler}
                  error={form.email.hasError}
                  type="email"
                />
                {form.email.hasError && (
                  <FieldHelpText isError={true} className={classes.error}>
                    {form.email.errorText}
                  </FieldHelpText>
                )}
              </div>
              <div>
                <FieldLabel htmlFor="password" className={classes.inputLabel}>
                  <FormattedMessage id="signIn.root.passwordLabel" />
                </FieldLabel>
                <Input
                  name="password"
                  id="password"
                  variant={INPUT_VARIANT.grey}
                  size={INPUT_SIZE.size48}
                  fullWidth={true}
                  placeholder={intl.formatMessage({ id: 'signIn.root.password' })}
                  value={form.password.value}
                  onChange={inputChangeHandler}
                  onBlur={blurHandler}
                  error={form.password.hasError}
                  type="password"
                />
                {form.password.hasError && (
                  <FieldHelpText isError={true} className={classes.error}>
                    {form.password.errorText}
                  </FieldHelpText>
                )}
                {nonFieldError && (
                  <FieldHelpText isError={true} className={classes.error}>
                    {nonFieldError}
                  </FieldHelpText>
                )}
              </div>
            </div>
            <div className={classes.buttonGroup}>
              <Button
                fullWidth={true}
                size={BUTTON_SIZE.size40}
                type={BUTTON_TYPE.submit}
                label={intl.formatMessage({ id: 'common.signIn' })}
              />
              <Button
                size={BUTTON_SIZE.size40}
                variant={BUTTON_VARIANT.secondaryPurple}
                to={Routes.FORGOT_PASSWORD}
                label={intl.formatMessage({ id: 'signIn.root.forgotPassword' })}
              />
            </div>
          </form>
        </div>
        <SupportFooter />
      </div>
    </DocumentTitle>
  )
}

export default Signin
